import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ContentContainer } from "../../../components/layout";
import {
  Button,
  DownloadButton,
  LoadingComponent,
  Modal,
  MultiSelect,
  NoDataFoundTemplate,
  Select,
  Table,
  Textfield,
} from "../../../components/core";
import { NumberFormatBase } from "react-number-format";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  FILTER_PACKAGES,
  LOCKER_NAME_FOR_VIEW_PACKAGE_HISTORY,
  NEW_PACKAGE_HISTORY_QUERY_WITH_PAYMENT,
} from "../../../constants/graphql/view-package-history-queries";
import PrimereactDateRange from "../../../components/core/date-range-primereact";
import { formatDate2, formatDateYYYYMMDD } from "../../../algorithms/date";
import { QUERY_LOCKER_TYPE } from "../../../constants/graphql/drop-off-graphql";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import TotalRecords from "../../../components/core/total-records";
import MobileLockerCards from "../locker-clean-up/components/mobile-cards";
import MobileViewCards from "../../../components/core/mobile-view-card";
import { rowClassName } from "../../../components/layout/package-status";
import { ScrollTop } from "primereact/scrolltop";

// Constants
const SERVICES = {
  DROPOFF: { id: 1, name: "Dropoff" },
  SHIPOUT: { id: 2, name: "Shipout" },
  STORAGE: { id: 3, name: "Storage" },
};

const SERVICE_LIST = [
  { value: "1", name: "Dropoff" },
  { value: "2", name: "Shipout" },
  { value: "3", name: "Storage" },
];

const PACKAGE_STATES = {
  100: "New",
  110: "Pending",
  118: "Removed",
  121: "Overstay",
  128: "Claimed",
  130: "Take-Back",
  138: "Revoked",
  140: "For RTS",
  141: "RTS Overstay",
  148: "RTS",
  150: "Cancelled",
  158: "Cleared Out",
  300: "New",
  310: "Drop-Off",
  320: "Pending",
  328: "Claimed",
  350: "Cancelled",
};

const getPackageStatusTailwindClass = (stateNo) => {
  if ([128, 328].includes(stateNo))
    return "bg-green-500 text-white border-green-600";
  if ([110, 121, 320].includes(stateNo))
    return "bg-amber-500 text-white border-amber-600";
  if ([118, 138, 148, 150, 350].includes(stateNo))
    return "bg-red-500 text-white border-red-600";
  if ([100, 300, 310, 130, 140].includes(stateNo))
    return "bg-blue-500 text-white border-blue-600";
  return "bg-gray-500 text-white border-gray-600";
};

const ERROR_MESSAGES = {
  START_DATE_GREATER: "Start date cannot be greater than end date.",
  INVALID_START_DATE: "Start date is invalid",
  INVALID_END_DATE: "End date is invalid",
  SHIPOUT_WARNING:
    "Please note that the Shipout service has not been implemented yet, and as a result, you may receive an empty dataset.",
};

// Utility functions
const removeEmptyFields = (obj) => {
  const cleaned = { ...obj };
  Object.keys(cleaned).forEach((key) => {
    if (
      cleaned[key] === "" ||
      cleaned[key] === null ||
      cleaned[key] === undefined ||
      cleaned[key] === "-1"
    ) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

const formatDate = (inputDate) => {
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(inputDate).toLocaleDateString("en-US", options);
};

const getCurrentUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

export const getMaxDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date;
};

export const getMaxDateMonths = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return date;
};

const getServiceByStateNo = (stateNo) => {
  if (stateNo >= 100 && stateNo < 200) return SERVICES.DROPOFF.name;
  if (stateNo >= 200 && stateNo < 300) return SERVICES.SHIPOUT.name;
  return SERVICES.STORAGE.name;
};

// Custom hooks
const usePackageSearch = () => {
  const maxDate = useMemo(() => getMaxDateMonths(), []);
  const currentUser = useMemo(() => getCurrentUser(), []);

  const [searchCriteria, setSearchCriteria] = useState({
    operatorId: currentUser?.operator_id || "",
    startDate: maxDate,
    endDate: new Date(),
    recipientMobile: "",
    waybillNumber: "",
    lockerType: "-1",
    lockerId: [],
    services: "-1",
    doorId: "",
    packageStorageId: "",
    packageId: "",
  });

  const [isSearchClicked, setIsSearchClicked] = useState(false);

  const updateSearchCriteria = useCallback((updates) => {
    setSearchCriteria((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetSearchCriteria = useCallback(() => {
    setSearchCriteria({
      operatorId: currentUser?.operator_id || "",
      startDate: maxDate,
      endDate: new Date(),
      recipientMobile: "",
      waybillNumber: "",
      lockerType: "-1",
      lockerId: [],
      services: "-1",
      doorId: "",
      packageStorageId: "",
      packageId: "",
    });
    setIsSearchClicked(false);
  }, [maxDate, currentUser?.operator_id]);

  return {
    searchCriteria,
    updateSearchCriteria,
    resetSearchCriteria,
    isSearchClicked,
    setIsSearchClicked,
    maxDate,
  };
};

const useModal = () => {
  const [modal, setModal] = useState({
    icon: "",
    title: "",
    message: "",
    open: false,
  });

  const showModal = useCallback((config) => {
    setModal({
      icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
      ...config,
      open: true,
      actions: (
        <div className="w-full">
          <Button
            onClick={() => setModal((prev) => ({ ...prev, open: false }))}
            value="OK"
            className="p-2 w-full"
          />
        </div>
      ),
    });
  }, []);

  return { modal, showModal };
};

// Main component
export default function ViewPackageHistory() {
  const {
    searchCriteria,
    updateSearchCriteria,
    resetSearchCriteria,
    isSearchClicked,
    setIsSearchClicked,
    maxDate,
  } = usePackageSearch();

  console.log({ maxDate });
  const { modal, showModal } = useModal();

  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const dt = useRef(null);

  const [allLockersSelected, setAllLockersSelected] = useState(false);
  const [lockerType, setLockerType] = useState([]);
  const [lockerList, setLockerList] = useState([]);
  const [packageList, setPackageList] = useState([]);
  const [searchResultSentence, setSearchResultSentence] =
    useState("Search result for");

  // GraphQL queries
  // const { data: lockersData } = useQuery(LOCKER_NAME_FOR_VIEW_PACKAGE_HISTORY, {
  //   variables: { operatorId: searchCriteria.operatorId },
  //   fetchPolicy: "no-cache",
  // });
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);
  const [fetchLockerName, { data: lockerNameData }] = useLazyQuery(
    NEWEST_LOCKER_NAME_QUERY,
    {
      variables: {
        operatorId: operatorId,
      },
    },
  );

  const [filterPackageWithPayment, { loading, data }] = useLazyQuery(
    FILTER_PACKAGES,
    { fetchPolicy: "no-cache" },
  );

  // Event handlers
  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      if (name === "services" && value === "2") {
        showModal({
          title: "Warning",
          message: ERROR_MESSAGES.SHIPOUT_WARNING,
        });
      }

      updateSearchCriteria({ [name]: value });
    },
    [updateSearchCriteria, showModal],
  );

  const validateSearchCriteria = useCallback(() => {
    if (searchCriteria.startDate > searchCriteria.endDate) {
      showModal({
        title: "Error",
        message: ERROR_MESSAGES.START_DATE_GREATER,
      });
      return false;
    }

    if (!searchCriteria.startDate) {
      showModal({
        title: "Error",
        message: ERROR_MESSAGES.INVALID_START_DATE,
      });
      return false;
    }

    if (!searchCriteria.endDate) {
      showModal({
        title: "Error",
        message: ERROR_MESSAGES.INVALID_END_DATE,
      });
      return false;
    }

    return true;
  }, [searchCriteria, showModal]);

  const buildSearchResultSentence = useCallback(() => {
    const lockerTypeName =
      searchCriteria.lockerType === "-1"
        ? "ALL Locker types"
        : searchCriteria.lockerType === "1"
          ? "Parcel Locker"
          : "Fridge Locker";

    let lockerName;
    if (allLockersSelected) {
      lockerName = "ALL Lockers";
    } else {
      const lockerLabels = searchCriteria.lockerId
        .map((selectedLocker) => {
          const matchedLocker = lockerName.find(
            (item) => parseInt(item.value) === parseInt(selectedLocker.value),
          );
          return matchedLocker ? matchedLocker.label : null;
        })
        .filter((label) => label !== null);

      lockerName =
        lockerLabels.length > 0 ? lockerLabels.join(", ") : "ALL Lockers";
    }

    const serviceName =
      searchCriteria.services !== "-1"
        ? SERVICE_LIST.find((x) => x.value === searchCriteria.services)?.name
        : "ALL SERVICES";

    const recipientText = searchCriteria.recipientMobile
      ? ` Recipient Mobile ${searchCriteria.recipientMobile}`
      : "";

    const waybillText = searchCriteria.waybillNumber
      ? ` Waybill Number ${searchCriteria.waybillNumber}`
      : "";

    const doorText = searchCriteria.doorId
      ? ` Door: ${searchCriteria.doorId}`
      : "";

    const allPackagesText =
      !searchCriteria.recipientMobile && !searchCriteria.waybillNumber
        ? "ALL PACKAGES"
        : "";

    return `Search result for ${lockerTypeName}, ${lockerName} with ${serviceName}${recipientText}${waybillText}${doorText} ${allPackagesText} From ${formatDate(
      searchCriteria.startDate,
    )} To ${formatDate(searchCriteria.endDate)}`;
  }, [searchCriteria, lockerList]);

  const handleSearch = useCallback(async () => {
    if (!validateSearchCriteria()) return;

    setIsSearchClicked(true);
    const lockerIdValues = searchCriteria.lockerId.map(
      (locker) => locker.value,
    );

    const formattedCriteria = {
      ...searchCriteria,
      startDate: formatDateYYYYMMDD(searchCriteria.startDate),
      endDate: formatDateYYYYMMDD(searchCriteria.endDate),
      lockerId: lockerIdValues,
      waybillNumber: searchCriteria.waybillNumber?.trim(),
    };

    const cleanedCriteria = removeEmptyFields(formattedCriteria);

    try {
      await filterPackageWithPayment({ variables: cleanedCriteria });
      setSearchResultSentence(buildSearchResultSentence());

      // Reset dates to Date objects for display
      updateSearchCriteria({
        startDate: new Date(searchCriteria.startDate),
        endDate: new Date(searchCriteria.endDate),
      });
    } catch (error) {
      console.error("Search error:", error);
    }
  }, [
    searchCriteria,
    validateSearchCriteria,
    updateSearchCriteria,
    filterPackageWithPayment,
    buildSearchResultSentence,
  ]);

  // Effects
  // useEffect(() => {
  //   if (lockersData?.lockers) {
  //     const tempList = lockersData.lockers.map((locker) => ({
  //       value: locker.lockerId,
  //       name: locker.lockerName,
  //     }));
  //     setLockerList(tempList);
  //   }
  // }, [lockersData]);

  useEffect(() => {
    if (searchCriteria.lockerType !== "-1") {
      // fetchLockerName({
      // 	operatorId: operatorId,
      // 	selectedLockerType: selectedLockerType,
      // });
      fetchLockerName({
        variables: {
          operatorId: operatorId,
          lockerTypeId: searchCriteria.lockerType,
        },
      });
    }
  }, [searchCriteria.lockerType, operatorId]);

  useEffect(() => {
    if (data?.filterPackages) {
      if (data.filterPackages.length === 0) {
        setPackageList([]);
        return;
      }

      const transformedData = data.filterPackages.map((element) => {
        return {
          userId: element.userAccount.user.userId,

          packageId: element.packageId,
          partnerAgentMobile:
            element.partnerAgentMobile || element.senderMobile
              ? formatNumbers(
                  element.partnerAgentMobile || element.senderMobile,
                )
              : "",
          senderMobile: formatNumbers(element.senderMobile),
          lockerName: element.locker.lockerName,
          recipientMobile: formatNumbers(element.recipientMobile),
          packageDate: formatDate2(element.packageDate),
          waybillNumber: element.waybillNumber || "(Storage)",
          unmappedPackageStateNo: element.packageStateNo,
          packageStateNo: PACKAGE_STATES[element.packageStateNo],
          statusDate:
            element.packageStateNo === 128
              ? formatDate2(element.statusDate)
              : "-",

          agentId: element.partnerAgentId,
          service: getServiceByStateNo(element.packageStateNo),
          hasIntegration: element.partnerBusinessPartnerId != null,
          partnerName: element.partnerName ?? null,
        };
      });

      setPackageList(transformedData);
    }
  }, [data]);
  useEffect(() => {
    if (fetchLockerType.data) {
      let tempData = [];
      fetchLockerType.data.lockerTypes.forEach((element, index) => {
        tempData.push({
          value: element.lockerTypeId,
          name: element.lockerType,
        });
      });
      setLockerType(tempData);
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (lockerNameData) {
      // let tempData = [
      // 	{
      // 		value: "200",
      // 		name: "-- All --",
      // 	},
      // ];
      const tempData = lockerNameData.lockerInfo.map((element) => ({
        label: element.lockerName,
        value: element.lockerId,
      }));

      if (!allLockersSelected) {
        tempData.unshift({
          value: 0,
          label: "All Lockers",
        });
      }

      setLockerList(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    const selected = searchCriteria.lockerId;
    const allLockers = lockerList;

    const hasZeroValue = selected.some((item) => item.value === 0);
    if (hasZeroValue) {
      setAllLockersSelected(true);
    } else if (selected.length === 0) {
      setAllLockersSelected(false);
    } else if (selected.length !== allLockers.length) {
      setAllLockersSelected(false);
    } else {
      setAllLockersSelected(true);
    }
  }, [searchCriteria.lockerId, lockerList]);

  useEffect(() => {
    if (allLockersSelected) {
      const selectedLockers = lockerList.filter((locker) => locker.value !== 0);

      // setSelectedLockerName(selectedLockers);
      // setFilterVariables({
      //   ...filterVariables,
      //   selectedLockerName: selectedLockers,
      // });
      updateSearchCriteria({
        lockerId: selectedLockers,
      });
    }
  }, [allLockersSelected, lockerList]);

  console.log({ packageList });
  return (
    <ContentContainer
      containerTitle="View Package History"
      className={`overflow-y-auto relative h-full`}
    >
      {/* Search Form */}
      <div className="grid grid-cols-12 md:grid-cols-10 gap-2 mb-2">
        <div className="col-span-12 md:col-span-7">
          <h2 className={"font-bold w-full"}>Package Details:</h2>
          <div className="grid grid-cols-10 px-4 items-center space-y-1">
            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Recipient mobile number:</p>
            </div>
            <NumberFormatBase
              classNameParent={`col-span-10 md:col-span-6`}
              customInput={Textfield}
              maxLength={10}
              prefix="+63"
              name="recipientMobile"
              onChange={handleInputChange}
              value={searchCriteria.recipientMobile}
            />
            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Waybill number:</p>
            </div>
            <div className="col-span-10 md:col-span-6">
              <Textfield
                name="waybillNumber"
                onChange={handleInputChange}
                value={searchCriteria.waybillNumber}
              />
            </div>
            <div className="col-span-4">
              <p className="text-base">Door:</p>
            </div>
            <div className="col-span-6">
              <Textfield
                name="doorId"
                onChange={handleInputChange}
                value={searchCriteria.doorId}
                maxLength={3}
              />
            </div>

            {/* Storage ID */}
            <div className="col-span-4">
              <p className="text-base ">Storage ID:</p>
            </div>
            <div className="col-span-6">
              <Textfield
                name="packageStorageId"
                onChange={handleInputChange}
                value={searchCriteria.packageStorageId}
                maxLength={5}
              />
            </div>

            {/* Package ID */}
            <div className="col-span-4">
              <p className="text-base ">Package ID:</p>
            </div>
            <div className="col-span-6">
              <Textfield
                name="packageId"
                onChange={handleInputChange}
                value={searchCriteria.packageId}
                maxLength={5}
              />
            </div>
          </div>

          <h2 className={"font-bold w-full pt-2"}>Locker:</h2>
          <div className="grid grid-cols-10 px-4 items-center space-y-1">
            {/* locker type */}
            <div className="col-span-10 md:col-span-4">
              <p className="text-base ">Locker Type:</p>
            </div>
            <Select
              items={lockerType}
              className={"col-span-10 md:col-span-6"}
              name={"lockerType"}
              value={searchCriteria.lockerType}
              defaultOption={{ value: "-1", name: "-- ALL--" }}
              onChange={handleInputChange}
            />
            {/* locker name */}
            <div className="col-span-10 md:col-span-4">
              <p className="text-base ">Locker Name:</p>
            </div>
            <MultiSelect
              items={lockerList}
              name={"lockerId"}
              className={"col-span-10 md:col-span-6"}
              value={searchCriteria.lockerId}
              placeholder={`${
                searchCriteria.lockerType === "-1" ? "ALL Lockers" : "Select..."
              }`}
              disabled={searchCriteria.lockerType === "-1"}
              onChange={handleInputChange}
            />

            <div className="col-span-10 md:col-span-4">
              <p className="text-base ">Services:</p>
            </div>
            <div className="col-span-10 md:col-span-6">
              <Select
                name="services"
                value={searchCriteria.services}
                items={SERVICE_LIST}
                defaultOption={{ value: "-1", name: "All services" }}
                onChange={handleInputChange}
              />
            </div>
            {/* <div className="col-span-6"> */}
          </div>

          <h2 className={"font-bold w-full pt-2"}>Package Dropoff Date:</h2>
          <div className="grid grid-cols-8 px-4 items-center space-y-1">
            {/* <div className="col-span-2">Date Filter:</div> */}
            <div className="col-span-8">
              <PrimereactDateRange
                onChange={handleInputChange}
                startDateName="startDate"
                endDateName="endDate"
                readOnlyInput={true}
                max={new Date()}
                min={maxDate}
                endDate={searchCriteria.endDate}
                startDate={searchCriteria.startDate}
              />
            </div>
          </div>
        </div>

        <div className="col-span-10"></div>

        {/* Search Button */}
        <div className="col-span-8 md:col-span-12">
          <div className="grid grid-cols-4 gap-4">
            <Button
              disabled={loading}
              onClick={handleSearch}
              className="col-span-3 md:col-span-1 h-10"
              value="Search"
            />

            {/* Reset Button */}
            {/* <div className="grid items-center grid-cols-2 w-1/2 gap-y-2"> */}
            <button
              type="button"
              className="flex items-center gap-2 hover:text-blue-500 cursor-pointer underline"
              onClick={resetSearchCriteria}
            >
              <i className="fas fa-undo my-auto"></i>
              <p className="ml-1">Reset</p>
            </button>
            {/* </div> */}
          </div>
        </div>
      </div>

      {/* Results Section */}
      {loading ? (
        <LoadingComponent />
      ) : isSearchClicked ? (
        packageList.length > 0 ? (
          <>
            <Card className="hidden md:grid">
              <div className="grid grid-cols-2 justify-between bg-primary-primaryYellow items-center">
                <div className="pl-3 text-secondary-purpleGray">
                  <TotalRecords count={packageList.length} />
                </div>

                <DownloadButton dt={dt} csvOnly={true} />
              </div>
              <DataTable
                stripedRows
                header={
                  <div
                    // style={{
                    //   padding: "0px",
                    // }}
                    className="bg-primary-primaryYellow "
                  >
                    {searchResultSentence}
                  </div>
                }
                rows={10}
                pt={{
                  header: {
                    className:
                      "p-4 bg-primary-primaryYellow text-secondary-purpleGray",
                  },
                }}
                paginator
                sortMode="multiple"
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Rows per page"
                value={packageList}
                className="text-center bg-primary-primaryYellow"
                removableSort
                cellSelection={true}
                ref={dt}
                onCellClick={(e) => {
                  // Determine which column was clicked based on e.field value
                  const clickedField = e.field;
                  console.log({ clickedField, ROW: e.rowData });
                  switch (clickedField) {
                    case "waybillNumber":
                      // Handle waybill number column click
                      if (e.rowData.packageId) {
                        window.open(
                          `/operator/admin/view-package-history/${e.rowData.packageId}/${e.rowData.userId}`,
                          "_blank",
                        );
                      }
                      break;

                    case "recipientMobile":
                      // Handle recipient mobile column click
                      if (e.rowData.userId) {
                        window.open(
                          `/operator/admin/locker-user-accounts/user-profile-page/${e.rowData.userId}`,
                          "_blank",
                        );
                      }
                      break;

                    case "partnerAgentMobile":
                      // Handle sender mobile column click

                      // if (e.rowData.senderId) {
                      //
                      if (e.rowData.service === "Storage") {
                        window.open(
                          `/operator/admin/locker-user-accounts/user-profile-page/${e.rowData.userId}`,
                          "_blank",
                        );
                      } else {
                        window.open(
                          `/operator/admin/agents/agentProfile/${e.rowData.agentId}`,
                          "_blank",
                        );
                      }
                      // }
                      break;

                    default:
                      // No specific action for other columns
                      break;
                  }
                }}
              >
                <Column
                  header="Package Status"
                  sortable
                  field="packageStateNo"
                  alignHeader={"center"}
                  className="text-center border-x-2"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                  body={(rowData) => (
                    <>
                      <Tag className={rowClassName(rowData.packageStateNo)}>
                        {rowData.packageStateNo}
                      </Tag>
                    </>
                  )}
                />
                <Column
                  header="Locker Name"
                  sortable
                  field="lockerName"
                  alignHeader={"center"}
                  className="text-center border-x-2"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                />
                <Column
                  header="Service Type"
                  sortable
                  field="service"
                  alignHeader={"center"}
                  className="text-center border-x-2"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                />
                <Column
                  header="Recipient Mobile Number"
                  field="recipientMobile"
                  alignHeader={"center"}
                  className="text-center border-x-2 cursor-pointer hover:text-blue-500"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray "
                />
                <Column
                  header="Sender Mobile Number"
                  field="partnerAgentMobile"
                  alignHeader={"center"}
                  className="text-center border-x-2 cursor-pointer hover:text-blue-500"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                  body={(rowData) => (
                    <div className="flex flex-col items-center gap-0.5">
                      <span>{rowData.partnerAgentMobile}</span>
                      {rowData.hasIntegration && (
                        <span className="flex items-center gap-1 text-[11px] text-sky-600 font-medium">
                          <i
                            className="pi pi-verified text-sky-500"
                            style={{ fontSize: "0.75rem" }}
                          />
                          {rowData.partnerName ?? "Integrated Partner"}
                        </span>
                      )}
                    </div>
                  )}
                />
                <Column
                  header="Waybill Number"
                  field="waybillNumber"
                  alignHeader={"center"}
                  className="text-center border-x-2 cursor-pointer hover:text-blue-500 max-w-[200px] break-words"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                />
                <Column
                  header="Package Dropoff Date"
                  field="packageDate"
                  sortable
                  alignHeader={"center"}
                  className="text-center border-x-2"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                />
                <Column
                  header="Package Claim Date"
                  alignHeader={"center"}
                  sortable
                  field="statusDate"
                  className="text-center border-x-2"
                  headerClassName="bg-primary-primaryYellow border-x-2 text-secondary-purpleGray"
                  // body={(rowData) => {
                  //   return (
                  //     <>
                  //       <div>
                  //         {rowData.packageStateNo === 128
                  //           ? rowData.statusDate
                  //           : "-"}
                  //       </div>
                  //     </>
                  //   );
                  // }}
                />
              </DataTable>
            </Card>
            {/* <p className="mt-4">
              Total Records: <b>{packageList.length}</b>
            </p>
            <div
              className="bg-primary-primaryYellow w-full h-24 border-2 text-center"
              style={{ borderColor: "#E6E6E6" }}
            >
              <p className="font-bold text-lg m-auto p-2">
                {searchResultSentence}
              </p>
            </div>
            <Table
              headers={tableHeaders}
              items={packageList}
              isForPackageHistory={true}
            /> */}
            <MobileViewCards data={packageList} isSelectable={true} />
          </>
        ) : (
          <NoDataFoundTemplate width={56} height={56} />
        )
      ) : null}

      <ScrollTop
        target="window"
        threshold={100}
        className="text-xs rounded-full p-3 bg-primary-primaryYellow md:hidden"
        icon="pi pi-arrow-up text-secondary-purpleGray"
      />

      <Modal
        icon={modal.icon}
        title={modal.title}
        message={modal.message}
        open={modal.open}
        actions={modal.actions}
      />
    </ContentContainer>
  );
}

export const formatNumbers = (mobileNumber) => {
  if (!mobileNumber || mobileNumber.length <= 2) return mobileNumber;
  return mobileNumber.toString().substring(2);
};
